import { useEffect, useState } from "react";
import { FolderInput, Copy, Trash2, Pencil, Download, X, Eye, Folder, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const Act = ({ icon: Icon, label, onClick, testId, danger }) => (
  <button onClick={onClick} className={`min-w-0 flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-1 sm:px-3 h-14 sm:h-10 rounded-lg text-[10px] sm:text-sm font-medium transition-colors ${danger ? "text-rose-300 hover:bg-rose-500/15" : "text-slate-100 hover:bg-white/10"}`} data-testid={testId}>
    <Icon className="h-4 w-4" />{label}
  </button>
);

export function ActionBar({ items, onOpen, onRename, onMove, onCopy, onDownload, onDelete, onClear }) {
  if (!items.length) return null;
  const onlyFiles = items.every((i) => i.type === "file");
  return (
    <div className="fixed z-[55] inset-x-0 mx-auto bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:bottom-8 w-[calc(100%-1.5rem)] sm:w-fit max-w-[calc(100%-1.5rem)] rounded-2xl border border-teal-500/40 bg-[#33373f]/95 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] px-2 py-1.5 flex flex-col sm:flex-row sm:items-center gap-1 fade-up" data-testid="drive-action-bar">
      <div className="flex items-center justify-between sm:contents">
        <span className="px-2 text-sm font-semibold text-teal-300 whitespace-nowrap sm:order-1" data-testid="drive-selection-count">{items.length} markiert</span>
        <button onClick={onClear} className="h-9 w-9 shrink-0 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10 sm:order-3 sm:ml-auto" data-testid="action-clear"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-flow-col auto-cols-fr sm:flex sm:items-center sm:order-2">
        {items.length === 1 && <Act icon={Eye} label="Öffnen" onClick={onOpen} testId="action-open" />}
        {items.length === 1 && <Act icon={Pencil} label="Umbenennen" onClick={onRename} testId="action-rename" />}
        <Act icon={FolderInput} label="Verschieben" onClick={onMove} testId="action-move" />
        {onlyFiles && <Act icon={Copy} label="Kopieren" onClick={onCopy} testId="action-copy" />}
        {onlyFiles && <Act icon={Download} label="Laden" onClick={onDownload} testId="action-download" />}
        <Act icon={Trash2} label="Löschen" onClick={onDelete} testId="action-delete" danger />
      </div>
    </div>
  );
}

export function FolderPicker({ clientId, mode, exclude = [], onPick, onClose }) {
  const [folders, setFolders] = useState([]);
  const [target, setTarget] = useState(undefined);
  useEffect(() => { api.get("/folders", { params: { client_id: clientId } }).then((r) => setFolders(r.data)); }, [clientId]);
  const blocked = new Set(exclude);
  let grew = true;
  while (grew) { grew = false; folders.forEach((f) => { if (blocked.has(f.parent_id) && !blocked.has(f.id)) { blocked.add(f.id); grew = true; } }); }
  const rows = [];
  const walk = (pid, depth) => folders.filter((f) => f.parent_id === pid && !blocked.has(f.id)).forEach((f) => { rows.push({ ...f, depth }); walk(f.id, depth + 1); });
  walk(null, 0);
  const Row = ({ id, name, depth }) => (
    <button onClick={() => setTarget(id)} style={{ paddingLeft: 12 + depth * 18 }} className={`w-full flex items-center gap-2 h-11 pr-3 rounded-lg text-sm text-left transition-colors ${target === id ? "bg-teal-500/20 text-teal-200 ring-1 ring-teal-500/50" : "hover:bg-white/5"}`} data-testid={`picker-folder-${id || "root"}`}>
      {id ? <Folder className="h-4 w-4 text-teal-400 shrink-0" /> : <FolderOpen className="h-4 w-4 text-slate-400 shrink-0" />}<span className="truncate">{name}</span>
    </button>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="dark text-foreground w-[95vw] max-w-md" data-testid="folder-picker">
        <DialogHeader><DialogTitle>{mode === "copy" ? "Kopieren nach…" : "Verschieben nach…"}</DialogTitle></DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-0.5 -mx-1 px-1">
          <Row id={null} name="Alle Ordner (oberste Ebene)" depth={0} />
          {rows.map((f) => <Row key={f.id} id={f.id} name={f.name} depth={f.depth + 1} />)}
        </div>
        <Button onClick={() => onPick(target)} disabled={target === undefined} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="picker-confirm">{mode === "copy" ? "Hierhin kopieren" : "Hierhin verschieben"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function RenameDialog({ name, onSave, onClose }) {
  const [v, setV] = useState(name);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="dark text-foreground w-[95vw] max-w-md" data-testid="rename-dialog">
        <DialogHeader><DialogTitle>Umbenennen</DialogTitle></DialogHeader>
        <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && v.trim() && onSave(v)} data-testid="rename-input" />
        <Button onClick={() => onSave(v)} disabled={!v.trim()} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="rename-confirm">Speichern</Button>
      </DialogContent>
    </Dialog>
  );
}
